import { useEffect, useState } from 'react';
import { Box, Card } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { AssetsPaymentsService, CashFlowService, LoanBalancesService, RevenueService } from '/src/api';
import { PieChart } from '/src/components';
import { useFilterStore } from '/src/context';
import {
  TGetAssetsPaymentsReportRequest,
  TGetCashFlowReportRequest,
  TGetLoanBalancesReportRequest,
  TGetRevenueReportRequest,
} from '/src/lib/types';

export const CompanyTotals: React.FC = observer(() => {
  /** Context **/
  const filterStore = useFilterStore();

  /** State **/
  const [isResolved, setIsResolved] = useState(false);
  const [balances, setBalances] = useState<any[]>();
  const [costs, setCosts] = useState<any[]>();
  const [assets, setAssets] = useState<any[]>();
  const [advances, setAdvances] = useState<any[]>();

  /** Computed **/
  const hasData = !!balances?.length || !!costs?.length || !!assets?.length || !!advances?.length;

  /** Methods **/

  /**
   * Gets loan balances info
   */
  const getBalances = async (query: TGetLoanBalancesReportRequest) => {
    setBalances(await LoanBalancesService.get(query));
  };

  /**
   * Gets revenue/costs info
   */
  const getCosts = async (query: TGetRevenueReportRequest) => {
    setCosts(await RevenueService.get(query));
  };

  /**
   * Gets assets infoe}
   */
  const getAssets = async (query: TGetAssetsPaymentsReportRequest) => {
    setAssets(await AssetsPaymentsService.get(query));
  };

  /**
   * Gets advances/cash flow info
   */
  const getAdvances = async (query: TGetCashFlowReportRequest) => {
    setAdvances(await CashFlowService.get(query));
  };

  /**
   * @name activateController()
   * @desc Performs any necessary init tasks
   */
  // const activateController = () => {};

  /** Effects **/
  useEffect(() => {
    const query: Record<string, any> = {};
    if (filterStore.lenderGet()) query.lenderId = filterStore.lenderGet()?._id;

    const fetchData = async () => {
      setIsResolved(false);
      await Promise.all([getBalances(query), getCosts(query), getAssets(query), getAdvances(query)]);
      setIsResolved(true);
    };

    fetchData();
  }, [filterStore._lenderFilterId]);

  /** Render **/
  return (
    <Card component="md-card">
      <Box display="flex" justifyContent="center" alignItems="center" className="toolbar-div">
        <span className="thin md-title">Company Totals</span>
      </Box>

      {hasData && (
        <Box
          display="flex"
          justifyContent="space-around"
          alignItems="center"
          bgcolor="white"
          className="pad-top-bottom-xs"
        >
          {/* BALANCES BY COMPANY */}
          <PieChart
            chartType="donut"
            chartTitle="balances"
            chartData={balances}
            hoverText="company"
            hoverValue="amount"
            showText={true}
            sortBy="amount"
          />

          {/* COSTS BY COMPANY (FEES + INTEREST) */}
          <PieChart
            chartType="donut"
            chartTitle="revenue"
            chartData={costs}
            hoverText="company"
            hoverValue="amount"
            showText={true}
            sortBy="amount"
          />

          {/* CURRENT ASSET VALUES BY COMPANY */}
          <PieChart
            chartType="donut"
            chartTitle="assets"
            chartData={assets}
            hoverText="company"
            hoverValue="amount"
            showText={true}
            sortBy="amount"
          />

          {/* ADVANCES BY COMPANY */}
          <PieChart
            chartType="donut"
            chartTitle="advances"
            chartData={advances}
            hoverText="company"
            hoverValue="amount"
            showText={true}
            sortBy="amount"
          />
        </Box>
      )}

      {isResolved && !hasData && (
        <Box display="row" justifyContent="center" alignItems="center" className="md-headline thin pad-top-bottom">
          No Companies Found.
        </Box>
      )}
    </Card>
  );
});
